import { useState } from 'react';
import {
  TrendingUp,
  DollarSign,
  Handshake,
  BarChart3,
  X,
} from 'lucide-react';
import {
  useForecast,
  useQuotas,
  useSetQuota,
  useQuotaProgress,
} from '../hooks/useForecasting';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface ForecastingViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ForecastingView({ onNavigate }: ForecastingViewProps) {
  const [period, setPeriod] = useState<string>('');
  const [showQuotaForm, setShowQuotaForm] = useState(false);
  const [quotaForm, setQuotaForm] = useState({ userId: '', period: '', amount: '' });

  const { data: forecast, isLoading } = useForecast(period || undefined);
  const { data: quotasData } = useQuotas(period || undefined);
  const { data: progressData } = useQuotaProgress(period || undefined);
  const setQuota = useSetQuota();

  function handleSetQuota() {
    setQuota.mutate(
      { userId: quotaForm.userId, period: quotaForm.period, amount: Number(quotaForm.amount) },
      {
        onSuccess: () => {
          setShowQuotaForm(false);
          setQuotaForm({ userId: '', period: '', amount: '' });
        },
      },
    );
  }

  // Compute max for bar scaling
  const maxMonthlyValue = forecast?.byMonth?.reduce((max, m) => Math.max(max, m.unweightedValue), 0) ?? 0;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Forecasting</h1>
          <p className="mt-0.5 text-sm text-slate-500">Sales pipeline forecast and quota tracking</p>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="grid grid-cols-4 gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card p-4">
                <div className="h-10 w-10 animate-pulse rounded-lg bg-surface-200" />
                <div className="mt-2 h-6 w-20 animate-pulse rounded bg-surface-200" />
              </div>
            ))}
          </div>
        ) : forecast ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Weighted Pipeline', value: formatCurrency(forecast.totalWeightedPipeline), icon: TrendingUp, color: 'text-brand-600 bg-brand-100' },
                { label: 'Unweighted Pipeline', value: formatCurrency(forecast.totalUnweightedPipeline), icon: DollarSign, color: 'text-green-600 bg-green-100' },
                { label: 'Deal Count', value: forecast.totalDealCount, icon: Handshake, color: 'text-blue-600 bg-blue-100' },
                { label: 'Avg Deal Size', value: formatCurrency(forecast.avgDealSize), icon: BarChart3, color: 'text-purple-600 bg-purple-100' },
              ].map((card) => (
                <div key={card.label} className="card p-4">
                  <div className="flex items-center gap-3">
                    <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', card.color)}>
                      <card.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                      <p className="text-xs text-slate-500">{card.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pipeline by Month Chart */}
            {forecast.byMonth && forecast.byMonth.length > 0 && (
              <div className="card p-6 mb-6">
                <h2 className="text-sm font-semibold text-slate-900 mb-4">Pipeline by Month</h2>
                <div className="flex items-end gap-3 h-48">
                  {forecast.byMonth.map((m) => {
                    const heightPercent = maxMonthlyValue > 0 ? (m.unweightedValue / maxMonthlyValue) * 100 : 0;
                    const weightedPercent = maxMonthlyValue > 0 ? (m.weightedValue / maxMonthlyValue) * 100 : 0;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex items-end gap-1 justify-center" style={{ height: '160px' }}>
                          <div
                            className="w-5 bg-brand-200 rounded-t"
                            style={{ height: `${heightPercent}%`, minHeight: heightPercent > 0 ? '4px' : '0' }}
                            title={`Unweighted: ${formatCurrency(m.unweightedValue)}`}
                          />
                          <div
                            className="w-5 bg-brand-600 rounded-t"
                            style={{ height: `${weightedPercent}%`, minHeight: weightedPercent > 0 ? '4px' : '0' }}
                            title={`Weighted: ${formatCurrency(m.weightedValue)}`}
                          />
                        </div>
                        <span className="text-2xs text-slate-500">{m.month}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded bg-brand-200" /> Unweighted</div>
                  <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded bg-brand-600" /> Weighted</div>
                </div>
              </div>
            )}

            {/* Pipeline Breakdown */}
            {forecast.byPipeline && forecast.byPipeline.length > 0 && (
              <div className="card overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-surface-200">
                  <h2 className="text-sm font-semibold text-slate-900">Pipeline Breakdown</h2>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-200 bg-surface-50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Pipeline</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Deals</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Unweighted</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Weighted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {forecast.byPipeline.map((p) => (
                      <tr key={p.pipelineId} className="hover:bg-surface-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{p.pipelineName}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{p.dealCount}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{formatCurrency(p.unweightedValue)}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">{formatCurrency(p.weightedValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Owner Breakdown */}
            {forecast.byOwner && forecast.byOwner.length > 0 && (
              <div className="card overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-surface-200">
                  <h2 className="text-sm font-semibold text-slate-900">Owner Breakdown</h2>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-200 bg-surface-50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">User</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Deals</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Weighted</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Quota</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">% of Quota</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {forecast.byOwner.map((o) => (
                      <tr key={o.userId} className="hover:bg-surface-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{o.userName}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{o.dealCount}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{formatCurrency(o.weightedValue)}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{o.quota ? formatCurrency(o.quota) : '--'}</td>
                        <td className="px-4 py-3 text-right">
                          {o.quota > 0 ? (
                            <div className="flex items-center justify-end gap-2">
                              <div className="h-2 w-16 rounded-full bg-surface-200">
                                <div
                                  className={cn(
                                    'h-2 rounded-full',
                                    o.quotaPercent >= 100 ? 'bg-green-500' : o.quotaPercent >= 75 ? 'bg-blue-500' : 'bg-orange-500',
                                  )}
                                  style={{ width: `${Math.min(o.quotaPercent, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-slate-600">{Math.round(o.quotaPercent)}%</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Quota Management */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-slate-900">Quota Management</h2>
                <button className="btn-secondary btn-sm" onClick={() => setShowQuotaForm(true)}>
                  Set Quota
                </button>
              </div>
              {quotasData?.quotas && quotasData.quotas.length > 0 ? (
                <div className="space-y-2">
                  {quotasData.quotas.map((q) => (
                    <div key={q.id} className="flex items-center justify-between rounded-lg border border-surface-200 px-4 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{q.userName ?? q.userId}</p>
                        <p className="text-xs text-slate-500">{q.period}</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(q.amount)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No quotas set. Click "Set Quota" to assign targets.</p>
              )}
            </div>
          </>
        ) : (
          <EmptyState
            icon={TrendingUp}
            title="No forecast data"
            description="Create deals in your pipeline to generate forecast data."
            actionLabel="Go to Deals"
            onAction={() => onNavigate('deals')}
          />
        )}
      </div>

      {/* Set Quota Modal */}
      {showQuotaForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Set Quota</h2>
              <button onClick={() => setShowQuotaForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label mb-1">User ID</label>
                <input className="input" value={quotaForm.userId} onChange={(e) => setQuotaForm((f) => ({ ...f, userId: e.target.value }))} placeholder="user-id" />
              </div>
              <div>
                <label className="label mb-1">Period</label>
                <input className="input" value={quotaForm.period} onChange={(e) => setQuotaForm((f) => ({ ...f, period: e.target.value }))} placeholder="e.g. 2026-Q2" />
              </div>
              <div>
                <label className="label mb-1">Amount</label>
                <input className="input" type="number" value={quotaForm.amount} onChange={(e) => setQuotaForm((f) => ({ ...f, amount: e.target.value }))} placeholder="100000" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowQuotaForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSetQuota} disabled={setQuota.isPending || !quotaForm.userId || !quotaForm.period || !quotaForm.amount}>
                {setQuota.isPending ? 'Saving...' : 'Set Quota'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
